import { createReducer, on } from '@ngrx/store';
import { Facets } from '@wam/shared';
import { keys } from 'lodash-es';

import * as assessmentsActions from './assessments.actions';
import { Params } from '@angular/router';
import { hydrateSelectedFacets } from '@app/shared/facets-transformers';
import { AssessmentsState } from './assessments.state';

function extractFacets(queryParams: Params): Facets {
  const facets = {};
  Object.keys(queryParams).forEach((key) => {
    const value = queryParams[key];
    facets[key] = value.split(',').map((facetValue) => ({
      value: facetValue,
      display: facetValue,
    }));
  });
  return facets ?? {};
}

const initialState: AssessmentsState = {
  level: null,
  hits: {},
  totalHits: 0,
  facets: {},
  selectedFacets: {},
  page: 1,
  classHasStudents: {},
  loading: false,
  studentAssignments: {},
  classAssignments: {},
  schoolAssignments: {},
};

const reducer = createReducer(
  initialState,
  on(assessmentsActions.load, (state) => ({
    ...state,
    page: 1,
  })),
  on(assessmentsActions.loadSuccess, (state, { hits, totalHits }) => ({
    ...state,
    hits: {
      ...state.hits,
      [state.level]: hits,
    },
    totalHits,
  })),
  on(assessmentsActions.continueLoadSuccess, (state, { hits, totalHits }) => ({
    ...state,
    totalHits,
    // @ts-ignore
    hits: {
      ...state.hits,
      [state.level]: [...state.hits[state.level], ...hits],
    },
  })),
  on(assessmentsActions.updateSelectedFacets, (state, { selectedFacets }) => ({
    ...state,
    page: 1,
    classes: [],
    selectedFacets,
  })),
  on(assessmentsActions.updateFacets, (state, { facets }) => ({
    ...state,
    facets,
    selectedFacets: hydrateSelectedFacets(facets, state.selectedFacets),
  })),
  on(assessmentsActions.continueLoad, (state) => ({
    ...state,
    page: state.page + 1,
  })),
  on(assessmentsActions.updateLevel, (state, { level }) => {
    const newHits = {
      ...state.hits,
      [state.level]: state.hits[state.level],
    };
    if (keys(state.selectedFacets).length > 0) {
      delete newHits[state.level];
    }
    return {
      ...state,
      level,
      hits: newHits,
      facets: {},
      selectedFacets: {},
      page: 1,
    };
  }),
  on(assessmentsActions.updateStoreFromRoute, (state, { view, queryParams }) => {
    const selectedFacets = extractFacets(queryParams);
    return {
      ...state,
      level: view,
      selectedFacets,
    };
  }),
  on(assessmentsActions.loadStudentAssignments, (state) => ({
    ...state,
    loading: true,
  })),
  on(assessmentsActions.loadClassAssignments, (state) => ({
    ...state,
    loading: true,
  })),
  on(assessmentsActions.loadSchoolAssignments, (state) => ({
    ...state,
    loading: true,
  })),
  on(assessmentsActions.loadStudentAssignmentsSuccess, (state, { userId, assignments }) => ({
    ...state,
    loading: false,
    studentAssignments: {
      ...state.studentAssignments,
      [userId]: assignments,
    },
  })),
  on(assessmentsActions.loadClassAssignmentsSuccess, (state, { classId, assignments }) => ({
    ...state,
    loading: false,
    classAssignments: {
      ...state.classAssignments,
      [classId]: assignments,
    },
  })),
  on(
    assessmentsActions.loadSchoolAssignmentsSuccess,
    (state, { schoolId, grade, assignments }) => ({
      ...state,
      loading: false,
      schoolAssignments: {
        ...state.schoolAssignments,
        [`${schoolId}_${grade}`]: assignments,
      },
    }),
  ),
);

export function assessmentsReducer(state, action) {
  return reducer(state, action);
}
